import { useEffect, useState } from 'react'
import {
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
} from 'firebase/auth'

import { auth } from '../lib/firebase.js'
import logo from '../assets/yesnet-logo.png'

// Real Firebase Auth gate. Admin must sign in with email + password
// configured in the Firebase Console (Authentication → Users). RTDB
// security rules require auth != null for every admin write, so an
// unauthenticated user can do nothing even if they bypass this UI.
export default function AuthGate({ children }) {
  const [user, setUser] = useState(undefined) // undefined = checking, null = signed-out, object = signed-in
  const [email, setEmail] = useState('')
  const [pw, setPw] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [info, setInfo] = useState('')

  useEffect(() => onAuthStateChanged(auth, (u) => setUser(u || null)), [])

  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-green text-white text-sm">
        Loading…
      </div>
    )
  }

  if (user) return children

  const submit = async (e) => {
    e.preventDefault()
    if (busy) return
    setErr(''); setInfo(''); setBusy(true)
    try {
      await signInWithEmailAndPassword(auth, email.trim(), pw)
    } catch (e) {
      setErr(friendlyAuthError(e))
    } finally {
      setBusy(false)
    }
  }

  const onReset = async () => {
    if (!email.trim()) { setErr('Enter your email above first, then click "Forgot password".'); return }
    setErr(''); setInfo(''); setBusy(true)
    try {
      await sendPasswordResetEmail(auth, email.trim())
      setInfo(`Password reset email sent to ${email.trim()}.`)
    } catch (e) {
      setErr(friendlyAuthError(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-green p-6">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm">
        <div className="text-center">
          <img src={logo} alt="YesNet" className="block mx-auto mb-3 max-h-20 object-contain" />
          <h1 className="font-extrabold text-lg">Admin sign in</h1>
          <p className="text-xs text-gray-500 mt-1">Sign in with your admin email and password.</p>
        </div>
        <div className="mt-4 space-y-3">
          <div>
            <label className="label">Email</label>
            <input
              type="email"
              autoFocus
              autoComplete="email"
              className="input"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setErr(''); setInfo('') }}
            />
          </div>
          <div>
            <label className="label">Password</label>
            <input
              type="password"
              autoComplete="current-password"
              className="input"
              value={pw}
              onChange={(e) => { setPw(e.target.value); setErr(''); setInfo('') }}
            />
          </div>
          {err ? <p className="text-xs text-red-600">{err}</p> : null}
          {info ? <p className="text-xs text-brand-green-deep">{info}</p> : null}
        </div>
        <button type="submit" disabled={!email || !pw || busy} className="btn-primary w-full mt-4">
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        <button
          type="button"
          onClick={onReset}
          disabled={busy}
          className="w-full mt-2 text-xs text-gray-500 hover:text-brand-green-deep underline">
          Forgot password?
        </button>
      </form>
    </div>
  )
}

export async function signOut() {
  try {
    await fbSignOut(auth)
  } catch {}
  window.location.reload()
}

function friendlyAuthError(e) {
  const code = e?.code || ''
  if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') {
    return 'Email or password is incorrect.'
  }
  if (code === 'auth/invalid-email') return 'That doesn’t look like a valid email.'
  if (code === 'auth/too-many-requests') return 'Too many attempts. Try again in a few minutes or use "Forgot password".'
  if (code === 'auth/network-request-failed') return 'Network error. Check your connection and try again.'
  if (code === 'auth/operation-not-allowed') {
    return 'Email/Password sign-in is not enabled yet in Firebase Console. Enable it under Authentication → Sign-in method.'
  }
  return e?.message || 'Sign in failed.'
}
